/** @format */
import { css } from 'styled-components';

import { smallTablet } from '@toucan/client/global/application';
import { theme } from '@toucan/client/global/theme';

import { StyledSidebarNavigationProps } from './index';

/**
 * Base styles for the sidebar navigation component
 *
 * @param {StyledSidebarNavigationProps} props The sidebar navigation properties object
 * */
/* eslint-disable @mpb-com/mpb/use-design-tokens, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
const baseStyles = ({ activeParentIndex }: StyledSidebarNavigationProps) => css`
    min-width: 24rem;
    border-bottom: 1px solid var(--colors-border-light);

    ul {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .sidebar-navigation-icon {
        display: inline-block;
        line-height: 0;
        margin-right: var(--layout-spacing-12);
        overflow: hidden;

        svg {
            width: 2rem;
            height: 2rem;
            fill: var(--colors-brand-primary);
        }
    }

    [data-depth='0'] > .sidebar-navigation-link {
        padding: var(--layout-spacing-12) var(--layout-spacing-16)
            var(--layout-spacing-12) 0;
        position: relative;
        width: 100%;

        &::after {
            display: none;
        }

        &::before {
            content: '';
            width: 1px;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            background: var(--colors-surface-accent);
            opacity: 0;
        }

        span {
            display: flex;
            align-items: center;
        }
    }

    [data-depth='0'][data-index='${activeParentIndex}']
        > .sidebar-navigation-link {
        ${theme.typography.body_normal_highlight}
        padding: var(--layout-spacing-12) var(--layout-spacing-16);

        &::before {
            opacity: 1;
        }
    }

    [data-depth='0'][data-index='${activeParentIndex}']
        > .sidebar-navigation-sub-menu {
        display: block;
    }

    .sidebar-navigation-link--active {
        font-weight: 600;
    }

    .sidebar-navigation-sub-menu {
        display: none;
        padding-left: var(--layout-spacing-48);

        .sidebar-navigation-link {
            margin-bottom: 0.8rem;
            ${theme.typography.body_small}
            color: var(--colors-text-secondary);
        }

        .sidebar-navigation-link--active {
            ${theme.typography.body_small_highlight}
            color: var(--colors-text-secondary);
        }
    }

    ${smallTablet(css`
        .sidebar-navigation-link {
            &:hover,
            &:focus {
                span {
                    /**
                    * These styles are inherited from the link component. We want them
                    * for the desktop version, otherwise we might not use that component.
                    * We do not want them for the mobile variant, and this is the most reliable
                    * way of unsetting them.
                    **/
                    color: var(--colors-surface-inverse) !important;
                    -webkit-background-clip: unset !important;
                    -moz-background-clip: unset !important;
                    -webkit-text-fill-color: unset !important;
                    -moz-text-fill-color: unset !important;
                    background-image: none !important;
                }

                &::after {
                    display: none;
                }
            }
        }

        .sidebar-navigation-sub-menu {
            padding: 0 0 var(--layout-spacing-12) 0;

            .sidebar-navigation-link {
                display: block;
                padding: calc(var(--layout-spacing-16) / 2) 0;
                margin: 0 var(--layout-spacing-12) 0 calc(var(--layout-spacing-12) + 2rem); // 2rem for icon
            }
        }

        [data-depth='0'] {
            border-bottom: 1px solid var(--colors-border-primary);
            margin: 0 var(--layout-spacing-24);

            & > .sidebar-navigation-link {
                padding: var(--layout-spacing-12) 0;
                &::before {
                    display: none;
                }
            }
        }

        [data-depth='0'][data-index='${activeParentIndex}'] > .sidebar-navigation-link {
            padding: var(--layout-spacing-12) 0;
        }
    }
    `)}
`;
/* eslint-enable @mpb-com/mpb/use-design-tokens, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */

/**
 * The sidebar navigation component styles
 *
 * @param {StyledSidebarNavigationProps} props The sidebar navigation properties object
 * */
export const styles = (props: StyledSidebarNavigationProps) => css`
    ${baseStyles(props)}
`;
